/**
 * <PolicyListPage /> — Session 3c shared generic policy page.
 *
 * Composes the locked Session 3c shell + the AI surfaces + a
 * DataTable-style listing. 27 of the 27 policy screens compose
 * from this generic page; the four special-shape screens
 * (Eligibility Matrix grid · Article 90 single-page form · GOSI
 * view-only · Geofence map picker) override the table area via
 * `customBody`.
 *
 * Per Commandment 73: NO window.alert. NO 'Live wiring lands'
 * phrasing. Uses showAlertTrace for popovers, ComingSoonBadge or
 * deep-link for unwired CTAs.
 *
 * Per Commandment 74: every visible string wrapped in t() with
 * an English fallback.
 */

import type { UseQueryResult } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  ComingSoonBadge,
  InlineAISuggestion,
  TraceableValue,
} from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import type { PolicyValueWithStack } from "../lib/session3c-api";
import { showAlertTrace } from "./_session3aHelpers";
import {
  M19PendingDraftsBanner,
  PolicyActionsRow,
  Session3cPolicyShell,
  buildOverrideStackTrace,
} from "./_session3cShell";
import type { Session3cAgent } from "./_session3cShell";

export interface PolicyColumn<R> {
  /** i18n key for the header. Fallback = label. */
  headerKey: string;
  label: string;
  render: (row: R) => ReactNode;
  /** If the value is a PolicyValueWithStack, render as TraceableValue. */
  traceableValue?: (row: R) => { label: string; stack: PolicyValueWithStack } | null;
}

export interface PolicyListPageProps<R> {
  title: string;
  subtitle: string;
  agent: Session3cAgent;
  /** Domain string for M19 routing + brief context. */
  domain: string;
  /** i18n-keyed label of the primary CTA (uses ComingSoonBadge for unwired). */
  newButtonLabel: string;
  /** When set to 'coming_soon' the + New CTA renders as ComingSoonBadge. */
  newButtonMode?: "live" | "coming_soon";
  /** Release window shown on the ComingSoonBadge when mode='coming_soon'. */
  newButtonExpectedRelease?: string;
  /** Live handler when mode='live'. */
  onNewClick?: () => void;
  /** Data query — should call session3cApi.list*. */
  query: UseQueryResult<{ items: R[] }, Error>;
  /** Optional column definitions (when omitted, customBody must render). */
  columns?: PolicyColumn<R>[];
  /** Row key extractor for the table. */
  rowKey?: (row: R) => string;
  /** Optional 1-2 inline AI suggestions to render above the table. */
  suggestions?: Array<{
    headline: string;
    body: string;
    severity?: "info" | "warning" | "critical";
    primaryActionLabel: string;
    primaryActionOnClick: () => void;
    suggestionKey: string;
  }>;
  /** Override the table area with a bespoke body (e.g., grid view). */
  customBody?: (items: R[]) => ReactNode;
  /** Pending M19 ingestion drafts for this domain. */
  pendingDraftsCount?: number;
  pendingDraftsSourceFile?: string;
  /** Brief context for the right-rail AI panel. */
  briefContext: {
    total: number;
    attention: number;
    critical: number;
    top_kind: string;
    window_label: string;
  };
}

export function PolicyListPage<R>(props: PolicyListPageProps<R>) {
  const { t } = useTranslation();
  const { gate } = useLegalEntityGate();
  const {
    title, subtitle, agent, domain,
    newButtonLabel, newButtonMode = "live", newButtonExpectedRelease,
    onNewClick,
    query,
    columns, customBody,
    rowKey,
    suggestions = [],
    pendingDraftsCount = 0, pendingDraftsSourceFile,
    briefContext,
  } = props;

  const items: R[] = query.data?.items ?? [];

  const actions = newButtonMode === "live" && onNewClick ? (
    <PolicyActionsRow
      domain={domain}
      newLabel={newButtonLabel}
      onNewClick={onNewClick}
      pendingDraftsCount={pendingDraftsCount}
    />
  ) : (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div />
      <div className="flex items-center gap-2">
        <ComingSoonBadge
          expectedRelease={newButtonExpectedRelease ?? "M3X.WORKFLOW"}
        >
          {newButtonLabel}
        </ComingSoonBadge>
      </div>
    </div>
  );

  if (gate) return gate;

  return (
    <Session3cPolicyShell
      title={title}
      subtitle={subtitle}
      agent={agent}
      briefContext={briefContext}
      actionsRow={actions}
    >
      {pendingDraftsCount > 0 && (
        <M19PendingDraftsBanner
          domain={domain}
          count={pendingDraftsCount}
          sourceFile={pendingDraftsSourceFile}
        />
      )}

      {suggestions.length > 0 && suggestions.map((s) => (
        <InlineAISuggestion
          key={s.suggestionKey}
          agent={agent}
          icon={<Sparkles className="h-4 w-4" />}
          severity={s.severity ?? "info"}
          headline={s.headline}
          body={s.body}
          primaryAction={{
            label: s.primaryActionLabel,
            onClick: s.primaryActionOnClick,
          }}
          suggestionKey={s.suggestionKey}
        />
      ))}

      {query.isLoading && <Loading />}
      {query.isError && <ErrorPanel message={(query.error as Error).message} />}

      {!query.isLoading && customBody && customBody(items)}

      {!query.isLoading && !customBody && columns && items.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
          <table className="w-full text-sm">
            <thead className="bg-surface-sunken">
              <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                {columns.map((c) => (
                  <th key={c.headerKey} className="px-3 py-2">
                    {t(c.headerKey, c.label)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((row, i) => (
                <tr
                  key={rowKey ? rowKey(row) : i}
                  className="group border-t border-border-subtle hover:bg-surface-sunken/40"
                >
                  {columns.map((c) => {
                    const trace = c.traceableValue?.(row);
                    if (trace) {
                      return (
                        <td key={c.headerKey} className="px-3 py-2">
                          <TraceableValue
                            onTraceClick={() =>
                              showAlertTrace(buildOverrideStackTrace(trace.label, trace.stack))
                            }
                          >
                            {c.render(row)}
                          </TraceableValue>
                        </td>
                      );
                    }
                    return (
                      <td key={c.headerKey} className="px-3 py-2">
                        {c.render(row)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!query.isLoading && !customBody && columns && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
          {t("policies.empty", "No rules yet. Use the buttons above to import from PDF or create one.")}
        </div>
      )}
    </Session3cPolicyShell>
  );
}
